import { isEmpty, isNotEmpty } from '@terros/common'
import { type MultiSelectFilterStateInput } from './multiSelectFilterValue'

export type MultiSelectFilterLookup<T extends string> = {
  isAllSelected: boolean
  selectedCount: number
  isSelected: (value: T) => boolean
  doesToggleSelectAll: (value: T) => boolean
}

const coversAllValues = <T extends string>(allValues: T[], selected: Set<T>): boolean => {
  for (const value of allValues) {
    if (!selected.has(value)) return false
  }
  return true
}

/**
 * Row flags for one selection. Built once so each option is checked in constant
 * time instead of simulating a toggle with array scans.
 */
export const createMultiSelectFilterLookup = <T extends string>({
  allValues,
  selectedValues,
  behavior,
}: MultiSelectFilterStateInput<T>): MultiSelectFilterLookup<T> => {
  const allValueSet = new Set(allValues)
  const explicitSelectedSet = new Set(selectedValues)
  const inScopeSelectedSet = new Set<T>()
  let preservedCount = 0
  for (const value of explicitSelectedSet) {
    if (allValueSet.has(value)) inScopeSelectedSet.add(value)
    else preservedCount += 1
  }

  const emptyMeansAll = behavior.emptyBehavior === 'all'
  const allowEmpty = behavior.emptyBehavior === 'all' || behavior.allowEmpty
  const selectionEmpty = isEmpty(selectedValues)
  const isAllSelected =
    isNotEmpty(allValues) && ((emptyMeansAll && selectionEmpty) || coversAllValues(allValues, inScopeSelectedSet))
  const selectedCount = isAllSelected ? allValues.length : inScopeSelectedSet.size
  const effectiveInScopeCount = emptyMeansAll && selectionEmpty ? allValues.length : inScopeSelectedSet.size

  const isSelected = (value: T): boolean => {
    if (!allValueSet.has(value)) return explicitSelectedSet.has(value)
    if (emptyMeansAll && selectionEmpty) return true
    return inScopeSelectedSet.has(value)
  }

  const doesToggleSelectAll = (value: T): boolean => {
    if (isEmpty(allValues)) return false
    if (!allValueSet.has(value)) {
      return doesOutOfScopeToggleSelectAll({
        value,
        explicitSelectedSet,
        emptyMeansAll,
        selectionEmpty,
        isAllSelected,
        allowEmpty,
      })
    }
    return doesInScopeToggleSelectAll({
      value,
      allValueCount: allValues.length,
      inScopeSelectedSet,
      effectiveInScopeCount,
      preservedCount,
      emptyMeansAll,
      selectionEmpty,
      allowEmpty,
    })
  }

  return { isAllSelected, selectedCount, isSelected, doesToggleSelectAll }
}

type OutOfScopeToggleInput<T extends string> = {
  value: T
  explicitSelectedSet: Set<T>
  emptyMeansAll: boolean
  selectionEmpty: boolean
  isAllSelected: boolean
  allowEmpty: boolean
}

const doesOutOfScopeToggleSelectAll = <T extends string>({
  value,
  explicitSelectedSet,
  emptyMeansAll,
  selectionEmpty,
  isAllSelected,
  allowEmpty,
}: OutOfScopeToggleInput<T>): boolean => {
  const removing = explicitSelectedSet.has(value)
  if (!removing) {
    if (emptyMeansAll && selectionEmpty) return false
    return isAllSelected
  }
  if (explicitSelectedSet.size > 1) return isAllSelected
  if (emptyMeansAll) return true
  return !allowEmpty
}

type InScopeToggleInput<T extends string> = {
  value: T
  allValueCount: number
  inScopeSelectedSet: Set<T>
  effectiveInScopeCount: number
  preservedCount: number
  emptyMeansAll: boolean
  selectionEmpty: boolean
  allowEmpty: boolean
}

const doesInScopeToggleSelectAll = <T extends string>({
  value,
  allValueCount,
  inScopeSelectedSet,
  effectiveInScopeCount,
  preservedCount,
  emptyMeansAll,
  selectionEmpty,
  allowEmpty,
}: InScopeToggleInput<T>): boolean => {
  const currentlySelected = (emptyMeansAll && selectionEmpty) || inScopeSelectedSet.has(value)
  const nextCount = currentlySelected ? effectiveInScopeCount - 1 : effectiveInScopeCount + 1
  if (nextCount === allValueCount) return true
  if (nextCount !== 0) return false
  if (preservedCount > 0) return false
  if (emptyMeansAll) return true
  return !allowEmpty
}
